import React, { useState } from 'react';
import { View, TouchableOpacity, Image as RNImage, ActivityIndicator, Platform } from 'react-native';
import { useForm } from 'react-hook-form';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import BottomSheet from '../../../../components/BottomSheet';
import Input from '../../../../components/Forms/Input';
import IconButton from '../../../../components/IconButton';
import useFormSubmit from '../../../../hooks/useFormSubmit';
import api from '../../../../utils/api';


export default function NewTicketModal({ visible, communityId, areas, categories = [], onRequestClose, onCreated }: any) {
  const { control, handleSubmit, watch, setValue, reset } = useForm<any>({
    defaultValues: { description: '', area_uuid: '', category_uuid: '' },
  });

  const [files, setFiles] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: (data) => {
      reset();
      setFiles([]);
      onCreated?.(data);
    },
    onError: () => {},
    successToastMessage: 'Reporte enviado',
    endpoint: `/communities/${communityId}/tickets`,
  });

  const description = watch('description');
  const categoryUuid = watch('category_uuid');

  const pickImage = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) {
      alert('Necesitamos permiso para acceder a tus fotos');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
      allowsMultipleSelection: false,
    });

    if (result.canceled || !result.assets?.[0]) return;
    await uploadAsset(result.assets[0]);
  };

  const takePhoto = async () => {
    const perm = await ImagePicker.requestCameraPermissionsAsync();
    if (!perm.granted) {
      alert('Necesitamos permiso de cámara');
      return;
    }

    const result = await ImagePicker.launchCameraAsync({
      quality: 0.7,
    });

    if (result.canceled || !result.assets?.[0]) return;
    await uploadAsset(result.assets[0]);
  };

  const uploadAsset = async (asset: ImagePicker.ImagePickerAsset) => {
    setUploading(true);
    try {
      const fd = new FormData();
      const uri = asset.uri;
      const name = uri.split('/').pop() ?? 'image.jpg';
      const ext = /\.(\w+)$/.exec(name)?.[1] ?? 'jpg';
      const type = `image/${ext}`;

      if (Platform.OS === 'web') {
        const blob = await (await fetch(uri)).blob();
        fd.append('image', blob, name);
      } else {
        // @ts-ignore RN file shape
        fd.append('image', { uri, name, type });
      }

      const res = await api.post(`/communities/${communityId}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFiles(prev => [...prev, res.data]);
    } catch (err: any) {
      alert(err?.response?.data?.message ?? 'Error subiendo imagen');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (uuid: string) => {
    setFiles(prev => prev.filter(f => f.uuid !== uuid));
  };

  const onSave = (data: any) => {
    submitForm({
      ...data,
      file_uids: files.map(f => f.uuid),
    });
  };

  return (
    <BottomSheet visible={visible} onRequestClose={onRequestClose} height={640}>
      <View style={tw`pt-5 px-5`}>
        <Text style={tw`mb-4 text-lg font-600 text-secondary`}>Nuevo reporte</Text>

        <Text style={tw`text-gray-500 mb-1`}>¿Qué pasa?</Text>
        <Input
          control={control}
          name="description"
          rules={{ required: true }}
          placeholder="Describe lo que quieres reportar"
          multiline
          numberOfLines={4}
        />

        {/* Categoría: solo si la comunidad deja al residente elegirla (ya filtrado por quien llama) */}
        {categories.length > 0 && (
          <>
            <Text style={tw`text-gray-500 mb-2`}>Categoría (opcional)</Text>
            <View style={tw`flex-row flex-wrap gap-2 mb-4`}>
              {categories.map((c: any) => (
                <AreaPill
                  key={c.uuid}
                  label={c.name}
                  color={c.color}
                  active={categoryUuid === c.uuid}
                  onPress={() => setValue('category_uuid', categoryUuid === c.uuid ? '' : c.uuid)}
                />
              ))}
            </View>
          </>
        )}

        {/* Evidencia */}
        <Text style={tw`text-gray-500 mb-2`}>Evidencia (opcional)</Text>
        <View style={tw`flex-row flex-wrap gap-2 mb-4`}>
          {files.map(f => (
            <View key={f.uuid} style={tw`relative`}>
              <RNImage source={{ uri: f.url }} style={tw`w-16 h-16 rounded-lg`} />
              <TouchableOpacity
                onPress={() => removeFile(f.uuid)}
                style={tw`absolute -top-1 -right-1 bg-white rounded-full border border-gray-200 w-5 h-5 items-center justify-center`}
              >
                <Ionicons name="close" size={12} color="#374151" />
              </TouchableOpacity>
            </View>
          ))}

          {uploading ? (
            <View style={tw`w-16 h-16 rounded-lg border border-dashed border-gray-300 items-center justify-center`}>
              <ActivityIndicator size="small" color="#6366f1" />
            </View>
          ) : (
            <>
              <TouchableOpacity
                onPress={takePhoto}
                style={tw`w-16 h-16 rounded-lg border border-dashed border-gray-300 items-center justify-center`}
              >
                <Ionicons name="camera-outline" size={20} color="#9ca3af" />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={pickImage}
                style={tw`w-16 h-16 rounded-lg border border-dashed border-gray-300 items-center justify-center`}
              >
                <Ionicons name="image-outline" size={20} color="#9ca3af" />
              </TouchableOpacity>
            </>
          )}
        </View>

        <IconButton
          disabled={isSubmitting || uploading || !description?.trim()}
          title="Enviar reporte"
          style={tw`self-end`}
          onPress={handleSubmit(onSave)}
        />
      </View>
    </BottomSheet>
  );
}

function AreaPill({ label, active, color, onPress }: any) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={tw.style(
        `px-3 py-1.5 rounded-full border`,
        active ? 'border-transparent bg-primary' : 'border-gray-200 bg-white',
        active && color ? { backgroundColor: color + '33', borderColor: 'transparent' } : null,
      )}
    >
      <Text style={tw.style(
        `text-xs font-500`,
        active ? 'text-white' : 'text-gray-700',
        active && color ? { color } : null,
      )}>
        {label}
      </Text>
    </TouchableOpacity>
  );
}
